import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Card,
} from '@mui/material';
import {
  VIEW_COLUMNS,
  VIEW_DATA_COLUMNS,
} from '../../config/module-configs/datachangelogs.config';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
// import SnackbarInfo from '../common/snackbar-info.component';
import ViewData from '../table-elements/view-data.component';
import { viewDataById } from '../../services/reports.service';
import ListData from '../table-elements/list-data.component';
import { formatTimestampToDateTime } from '../../utils/datetime.util';
import {
  WEEK_NAME,
  RECURRING_TYPE,
  QUESTION_TYPES,
} from '../../config/const.config';
import { SUBSCRIBER_ROLE } from '../../config/roles.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ViewDataChangeLogs = ({ title, user, dataId, onCancel }) => {
  const columns = VIEW_COLUMNS;
  const dataColunm = VIEW_DATA_COLUMNS;

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    data: '',
  });

  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handlePageChange = (newPage) => {
    setOptions((prev) => ({
      ...prev,
      page: newPage,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setOptions((prev) => ({
      ...prev,
      rowsPerPage: newRowsPerPage,
      page: 0,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const labelsToConvert = ['Week Numbers', 'Recurring Days'];

  const convertWeekString = (str) =>
    str
      .split(',')
      .map((num) => WEEK_NAME[Number(num.trim())])
      .filter(Boolean)
      .join(', ');

  const convertRecurringString = (str) => RECURRING_TYPE[Number(str.trim())];

  const getQuestionTypeLabel = (val) => {
    const type = Object.values(QUESTION_TYPES).find(
      (q) => q.value === Number(val)
    );
    return type?.label || val;
  };

  const formatBinaryValue = (val) => {
    if (val === '1') return 'Yes';
    if (val === '0') return 'No';
    return val;
  };

  useEffect(() => {
    viewDataById(dataId)
      .then((res) => {
        setRowData(res.data);
        res.data.changes = res.data.changes.map((item) => {
          if (labelsToConvert.includes(item.label)) {
            return {
              ...item,
              oldData:
                typeof item.oldData === 'string'
                  ? convertWeekString(item.oldData)
                  : item.oldData,
              newData:
                typeof item.newData === 'string'
                  ? convertWeekString(item.newData)
                  : item.newData,
            };
          }
          if (item.label === 'Recurring') {
            return {
              ...item,
              oldData: convertRecurringString(item.oldData),
              newData: convertRecurringString(item.newData),
            };
          }

          if (item.label === 'Role') {
            return {
              ...item,
              oldData: SUBSCRIBER_ROLE[item.oldData]?.label || item.oldData,
              newData: SUBSCRIBER_ROLE[item.newData]?.label || item.newData,
            };
          }

          if (item.label === 'Answer') {
            return {
              ...item,
              oldData: formatBinaryValue(item.oldData),
              newData: formatBinaryValue(item.newData),
            };
          }
          return item;
        });

        setLoading(false);
        const allChanges = (res?.data?.changes || []).map((change) => {
          if (change.label === 'Event Start Date') {
            return {
              ...change,
              oldData: formatTimestampToDateTime(change.oldData),
              newData: formatTimestampToDateTime(change.newData),
            };
          }
          if (change.label === 'Recurring End Date Time') {
            return {
              ...change,
              oldData: formatTimestampToDateTime(change.oldData),
              newData: formatTimestampToDateTime(change.newData),
            };
          }
          if (change.label === 'Gathering Start Date') {
            return {
              ...change,
              oldData: formatTimestampToDateTime(change.oldData),
              newData: formatTimestampToDateTime(change.newData),
            };
          }
          if (change.label === 'Quiz Start Date') {
            return {
              ...change,
              oldData: formatTimestampToDateTime(change.oldData),
              newData: formatTimestampToDateTime(change.newData),
            };
          }
          if (change.label === 'Quiz End Date') {
            return {
              ...change,
              oldData: formatTimestampToDateTime(change.oldData),
              newData: formatTimestampToDateTime(change.newData),
            };
          }

          if (change.label === 'Type') {
            return {
              ...change,
              oldData: getQuestionTypeLabel(change.oldData),
              newData: getQuestionTypeLabel(change.newData),
            };
          }
          if (change.label === 'Moderators') {
            return {
              ...change,
              oldData: Array.isArray(change.oldData)
                ? change.oldData.join('\n')
                : '',
              newData: Array.isArray(change.newData)
                ? change.newData.join('\n')
                : '',
            };
          }

          return change;
        });

        setOptions((prev) => ({
          ...prev,
          loading: false,
          totalRows: allChanges.length,
          rows: allChanges.slice(
            prev.page * prev.rowsPerPage,
            (prev.page + 1) * prev.rowsPerPage
          ),
          error: false,
        }));
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId, options.reloadCounter]);

  return (
    <Dialog
      open
      onClose={() => onCancel()}
      aria-labelledby="view-dialog-title"
      aria-describedby="view-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle
        id="view-dialog-title"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {title}
        {!!dataId && user.profilePic && (
          <Box
            component="img"
            sx={{
              height: 30,
              width: 30,
              objectFit: 'cover',
              borderRadius: 15,
              display: 'inline',
              marginX: 1,
            }}
            src={user.profilePic}
          />
        )}
        {!!dataId && user.email}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="view-dialog-description">
        {loading && <Loading />}
        {!loading && (
          <div>
            <ViewData columns={columns} row={rowData} />
            <Card sx={{ my: 2 }}>
              <ListData
                columns={dataColunm}
                rows={options.rows}
                page={options.page}
                rowsPerPage={options.rowsPerPage}
                totalRows={options.totalRows}
                loading={options.loading}
                actions={[]}
                error={options.error}
                sortBy={options.sortBy}
                sortOrder={options.sortOrder}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={() => {}}
                onAction={() => {}}
                tableName={rowData?.data}
              />
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

ViewDataChangeLogs.propTypes = {
  title: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ViewDataChangeLogs;
